import {
  format,
  startOfDay,
  endOfDay,
  startOfWeek,
  endOfWeek,
  startOfMonth,
  endOfMonth,
  subMonths,
  subWeeks,
  startOfYear,
} from "date-fns";

/** Week starts on Monday (1) for last_week and this_week. */
const WEEK_STARTS_ON = 1;

export type TimePreset =
  | "last_week"
  | "this_week"
  | "today"
  | "this_month"
  | "last_3_months"
  | "last_6_months"
  | "ytd"
  | "custom";

export interface TimePeriodRange {
  start: string;
  end: string;
}

/** Order for dropdown: Last week, This week, Today, then rest. */
export const TIME_PRESET_ORDER: TimePreset[] = [
  "last_week",
  "this_week",
  "today",
  "this_month",
  "last_3_months",
  "last_6_months",
  "ytd",
  "custom",
];

export const TIME_PRESET_LABELS: Record<TimePreset, string> = {
  last_week: "Last week",
  this_week: "This week",
  today: "Today",
  this_month: "This month",
  last_3_months: "Last 3 months",
  last_6_months: "Last 6 months",
  ytd: "Year to Date",
  custom: "Custom range",
};

/** Default preset: last week (previous Monday–Sunday). */
export const DEFAULT_TIME_PRESET: TimePreset = "last_week";

/**
 * Get date range for a preset. Last week / this week use Monday–Sunday.
 * custom requires explicit range (pass as second argument or use fallback).
 */
export function getDateRangeFromPreset(
  preset: TimePreset,
  customRange?: TimePeriodRange
): TimePeriodRange {
  const now = new Date();
  switch (preset) {
    case "last_week": {
      const prevWeek = subWeeks(now, 1);
      const start = startOfWeek(prevWeek, { weekStartsOn: WEEK_STARTS_ON });
      const end = endOfWeek(prevWeek, { weekStartsOn: WEEK_STARTS_ON });
      return {
        start: format(start, "yyyy-MM-dd"),
        end: format(end, "yyyy-MM-dd"),
      };
    }
    case "this_week": {
      const start = startOfWeek(now, { weekStartsOn: WEEK_STARTS_ON });
      const end = endOfWeek(now, { weekStartsOn: WEEK_STARTS_ON });
      return {
        start: format(start, "yyyy-MM-dd"),
        end: format(end, "yyyy-MM-dd"),
      };
    }
    case "today":
      return {
        start: format(startOfDay(now), "yyyy-MM-dd"),
        end: format(endOfDay(now), "yyyy-MM-dd"),
      };
    case "this_month":
      return {
        start: format(startOfMonth(now), "yyyy-MM-dd"),
        end: format(endOfMonth(now), "yyyy-MM-dd"),
      };
    case "last_3_months":
      return {
        start: format(subMonths(now, 3), "yyyy-MM-dd"),
        end: format(now, "yyyy-MM-dd"),
      };
    case "last_6_months":
      return {
        start: format(subMonths(now, 6), "yyyy-MM-dd"),
        end: format(now, "yyyy-MM-dd"),
      };
    case "ytd":
      return {
        start: format(startOfYear(now), "yyyy-MM-dd"),
        end: format(now, "yyyy-MM-dd"),
      };
    case "custom":
      if (customRange?.start && customRange?.end) {
        return customRange;
      }
      return getDateRangeFromPreset(DEFAULT_TIME_PRESET);
    default:
      return getDateRangeFromPreset(DEFAULT_TIME_PRESET);
  }
}

/** Format range for display, e.g. "Jan 27 – Feb 2, 2026". */
export function formatDateRangeDisplay(start: string, end: string): string {
  try {
    const startDate = new Date(start + "T12:00:00");
    const endDate = new Date(end + "T12:00:00");
    const sameYear = startDate.getFullYear() === endDate.getFullYear();
    const fmtStart = format(startDate, "MMM d");
    const fmtEnd = sameYear
      ? format(endDate, "MMM d, yyyy")
      : format(endDate, "MMM d, yyyy");
    return sameYear ? `${fmtStart} – ${fmtEnd}` : `${fmtStart}, ${startDate.getFullYear()} – ${fmtEnd}`;
  } catch {
    return `${start} – ${end}`;
  }
}
